import { reportingPlatform } from '@/lib/reporting-platforms';
/**
 * Shared constants for pipeline queries.
 * Keeps visibility and rank pages aligned on the same data limits.
 */
// One SQL page; dashboard requests subsequent pages until its inventory is complete.
export const BRAND_PROMPTS_BY_ENGINE_LIMIT = 5000;
export const PIPELINE_REQUEST_DEADLINE_MS = 45_000;
export const PROMPT_TOP_BRANDS_BY_ENGINE_LIMIT = 2400;

/** Canonical engine order for consistent display across visibility and rank pages */
export const ENGINE_ORDER = [
  'chatgpt',
  'claude',
  'gemini',
  'google_aio',
  'perplexity',
  'grok',
  'bing_copilot',
  'google_ai_mode',
];

export function sortEngines(engines: string[]): string[] {
  return [...new Set(engines.map(reportingPlatform))].sort((a, b) => {
    const aIdx = ENGINE_ORDER.indexOf(a.toLowerCase());
    const bIdx = ENGINE_ORDER.indexOf(b.toLowerCase());
    const aWeight = aIdx === -1 ? Number.MAX_SAFE_INTEGER : aIdx;
    const bWeight = bIdx === -1 ? Number.MAX_SAFE_INTEGER : bIdx;
    if (aWeight !== bWeight) return aWeight - bWeight;
    return a.localeCompare(b);
  });
}

/** Runnable engines; historical Claude remains display-only. */
export const SAMPLER_ENGINES = [
  'chatgpt',
  'gemini',
  'google_aio',
  'grok',
  'perplexity',
  'chatgpt_searchapi',
  'gemini_searchapi',
  'perplexity_searchapi',
  'bing_copilot',
  'google_ai_mode',
] as const;
export type SamplerEngine = (typeof SAMPLER_ENGINES)[number];
export const SEARCHAPI_DEFAULT_ENGINES: SamplerEngine[] = [
  'chatgpt_searchapi',
  'gemini_searchapi',
  'perplexity_searchapi',
  'bing_copilot',
  'google_ai_mode',
  'google_aio',
];
export const DEFAULT_SETUP_ENGINES: SamplerEngine[] = SEARCHAPI_DEFAULT_ENGINES;
export function normalizeEngineSelection(value: unknown): SamplerEngine[] {
  if (value === undefined) return [...DEFAULT_SETUP_ENGINES];
  if (
    !Array.isArray(value) ||
    !value.length ||
    value.some(engine => !SAMPLER_ENGINES.includes(engine))
  ) {
    throw new Error('Choose at least one supported engine');
  }
  return [...new Set(value)] as SamplerEngine[];
}
export const SEARCHAPI_ENGINE_LABELS: Record<string, string> = {
  chatgpt_searchapi: 'ChatGPT',
  gemini_searchapi: 'Gemini',
  perplexity_searchapi: 'Perplexity',
  bing_copilot: 'Bing Copilot',
  google_ai_mode: 'Google AI Mode',
};

/** Existing collection plans keep their implicit pre-selection providers. */
export function resolveProjectEngineSelection(project: {
  onboarding_job_id?: string | null;
  analysis_settings?: {
    engine_selection_version?: number;
    engines?: string[];
    prompts?: unknown[];
  } | null;
}): SamplerEngine[] {
  const settings = project.analysis_settings;
  if (settings?.engine_selection_version === 2) return normalizeEngineSelection(settings.engines);
  if (project.onboarding_job_id || settings?.prompts?.length || settings?.engines?.length) {
    return ['chatgpt', 'gemini', 'google_aio'];
  }
  return [...DEFAULT_SETUP_ENGINES];
}
